import { useEffect, useRef, useState } from "react";
import { useWindowSize } from "../Hooks/useWindowSize";

/**
 * @param {number} length
 * @returns {string}
 */
function generateRandomPassword(length = 16) {
  // String of all the characters to include in the password
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+";

  // Use the `Array.from()` function to create an array of `length` elements,
  // where each element is a random character from the character string
  const passwordArray = Array.from({ length }, () => characters[Math.floor(Math.random() * characters.length)]);

  // Use the `join()` method to join the array of characters into a single string
  return passwordArray.join("");
}

function useFocus() {
  const htmlElRef = useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
}

/**
 * @param {*} ref
 * @returns {[key: string, string]|null}
 */
export function getElementPosition(ref) {
  if (!ref) {
    return null;
  }
  const rect = ref?.current?.getBoundingClientRect();
  return { top: rect?.top, right: rect?.right, bottom: rect?.bottom, left: rect?.left };
}

/**  @returns {number} */
export function useSelectHeight(ref, minHeight = 224) {
  const [height, setHeight] = useState(minHeight);
  // top-100% or bottom-100%. Opposite of the side its supposed to open
  const [position, setPosition] = useState("top");
  const { height: windowHeight } = useWindowSize();

  useEffect(() => {
    if (!ref) {
      return height;
    }

    const { top, bottom } = getElementPosition(ref);

    //  Select margin * default app padding * Select height
    const buffer = 16 * 3;

    let calcHeight = windowHeight - bottom - buffer;

    if (calcHeight < height) {
      calcHeight = top - buffer;
      setPosition("bottom");
    }

    setHeight(calcHeight >= height ? calcHeight : height);
  }, [height, windowHeight, ref]);

  return { height, position };
}

function useDebounce(callback, delay) {
  const [timeoutId, setTimeoutId] = useState(null);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const debouncedCallback = (...args) => {
    clearTimeout(timeoutId);
    setTimeoutId(
      setTimeout(() => {
        callback(...args);
      }, delay)
    );
  };

  return debouncedCallback;
}

function getUUID() {
  return Date.now();
}

/**
 * @param {string} sortKey
 * @returns {{[key: string]: any}}
 */
// eslint-disable-next-line no-extend-native
Array.prototype.sortObject = function (sortKey) {
  return this.sort((a, b) => {
    if (a[sortKey].toLowerCase() > b[sortKey].toLowerCase()) {
      return 1;
    }
    if (a[sortKey].toLowerCase() < b[sortKey].toLowerCase()) {
      return -1;
    }
    return 0;
  });
};

/** @param {string} value */
export function copyToClipBoard(value) {
  navigator.clipboard.writeText(value);
}

export { generateRandomPassword, getUUID, useDebounce, useFocus };
